import { useSelector } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import { auth, observeIdToken } from '../../lib/firebase';
import { ADMIN_EMAIL } from '../../lib/env';
import { RootState } from '../store';

/**
 * Hook to check user entitlements and capabilities
 */
export function useEntitlements() {
  const { plan, status } = useSelector((state: RootState) => state.subscription);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [claimsVersion, setClaimsVersion] = useState(0);

  const readClaims = useCallback(async () => {
    if (!isAuthenticated) {
      setIsAdmin(false);
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsAdmin(false);
      return;
    }

    const idTokenResult = await currentUser.getIdTokenResult();
    const claimAdmin = idTokenResult.claims.isAdmin === true;
    const emailAdmin = (currentUser.email || '').toLowerCase() === ADMIN_EMAIL.toLowerCase();
    const adminStatus = claimAdmin || emailAdmin;
    setIsAdmin(adminStatus);

    if (adminStatus) {
      import('../../lib/telemetry').then(({ trackUsage }) => {
        trackUsage('usage_admin_seen').catch(console.warn);
      });
    }
  }, [isAuthenticated]);

  // Check admin status from ID token claims
  useEffect(() => {
    readClaims().catch(console.warn);
  }, [readClaims, claimsVersion]);

  useEffect(() => {
    const unsubscribe = observeIdToken(() => {
      setClaimsVersion(prev => prev + 1);
    });
    return unsubscribe;
  }, []);

  // Check if user has PRO entitlements (can write)
  const hasProAccess = () => {
    if (!isAuthenticated) return false;
    return plan === 'PRO-MONTHLY' || plan === 'PRO-ANNUAL' || plan === 'PRO_TRIAL';
  };

  // Check if user has ARCHIVE entitlements (read-only)
  const hasArchiveAccess = () => {
    if (!isAuthenticated) return false;
    return plan === 'ARCHIVE' || plan === 'ARCHIVE-ONE-TIME';
  };

  // Check if user can perform write operations
  const canWrite = () => {
    return hasProAccess();
  };

  // Check if user can read data
  const canRead = () => {
    return hasProAccess() || hasArchiveAccess();
  };

  // Check if user can generate reports
  const canGenerateReports = () => {
    return hasProAccess(); // ARCHIVE users cannot generate new reports
  };

  // Check if user can export data
  const canExport = () => {
    return hasProAccess() || hasArchiveAccess();
  };

  // Get user-friendly status message
  const getStatusMessage = () => {
    switch (plan) {
      case 'PRO-MONTHLY':
      case 'PRO-ANNUAL':
      case 'PRO_TRIAL':
        return null; // Full access
      case 'ARCHIVE':
      case 'ARCHIVE-ONE-TIME':
        return 'Read-only access: You can view and export your data but cannot add new expenses or generate reports.';
      case 'FREE':
      default:
        return 'Upgrade to Pro for full access to expense tracking and tax reporting.';
    }
  };

  // Get appropriate button text based on plan
  const getActionButtonText = (action: string, defaultText: string) => {
    if (canWrite()) return defaultText;

    switch (action) {
      case 'add':
        return 'Read-Only Mode';
      case 'edit':
        return 'View Only';
      case 'generate':
        return 'Export Only';
      default:
        return defaultText;
    }
  };

  return {
    plan,
    status,
    hasProAccess: hasProAccess(),
    hasArchiveAccess: hasArchiveAccess(),
    canWrite: canWrite(),
    canRead: canRead(),
    canGenerateReports: canGenerateReports(),
    canExport: canExport(),
    getStatusMessage: getStatusMessage(),
    getActionButtonText,
    isAuthenticated,
    isAdmin,
    claimsVersion,
    refreshClaims: async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.getIdToken(true);
      }
      setClaimsVersion(prev => prev + 1);
    },
  };
}
